if (!customElements.get('product-media-gallery')) {
  customElements.define(
    'product-media-gallery',
    class ProductMediaGallery extends HTMLElement {
      resizeObserver = null;

      constructor() {
        super();

        this.sliderElement = this.querySelector('.product-gallery-main-slider');
        this.sliderThumbnailElement = this.querySelector('.product-gallery-thumbnail-slider');
        this.nextButton = this.querySelector('.product-gallery-main .swiper-button-next');
        this.previousButton = this.querySelector('.product-gallery-main .swiper-button-prev');

        this.mainSlider = null;
        this.thumbnailSlider = null;

        this.mainSliderSettings = this.getMainSliderSettings();
        this.mainSliderOptions = this.getMainSliderOptions();
      }

      connectedCallback() {
        if (!this.sliderElement) {
          console.warn('Missing main slider element');
          return;
        }

        this.initSliders();
        this.bindVariantChange(this.dataset.currentMediaId);
        this.observeResize();
        this.onOptionChange = (event) => {
          const input = event.target;
          if (!input || input.tagName !== 'INPUT' || input.type !== 'radio') return;
          if (!input.dataset.position || !input.closest('.swatch')) return;
          if (input.closest('custom-corner-linked-product') || input.closest('.addons-products')) return;

          const variant = this.getSelectedVariant();
          if (variant) {
            const imageId = variant.featured_image && variant.featured_image.id;
            this.bindVariantChange(imageId, variant.id);
            return;
          }

          if (input.dataset.mediaId) this.bindVariantChange(input.dataset.mediaId);
        };
        document.addEventListener('change', this.onOptionChange);
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        window.visualViewport?.removeEventListener('resize', this.onGalleryResize);
        document.removeEventListener('change', this.onOptionChange);
        this.mainSlider?.destroy();
        this.thumbnailSlider?.destroy();
      }

      getMainSliderSettings() {
        try {
          return JSON.parse(this.dataset.sliderConfiguration || '{}');
        } catch (e) {
          console.warn('Invalid main slider configuration JSON', e);
          return {};
        }
      }

      getMainSliderOptions() {
        return {
          initialSlide: this.mainSliderSettings.initialSlide,
          effect: 'fade',
          fadeEffect: { crossFade: true },
          autoHeight: true,
          slidesPerView: 1,
          loop: this.mainSliderSettings.enableLoop || false,
          speed: this.mainSliderSettings.speed || 300,
          autoplay: Shopify.designMode
            ? false
            : this.mainSliderSettings.enableAutoplay
            ? {
                delay: this.mainSliderSettings.autoplayDelay || 7000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false,
          navigation:
            this.mainSliderSettings.enableNavigation && this.nextButton && this.previousButton
              ? {
                  nextEl: this.nextButton,
                  prevEl: this.previousButton,
                }
              : false,
          keyboard: { enabled: true, onlyInViewport: true },
          a11y: {
            enabled: true,
            prevSlideMessage: 'Previous slide',
            nextSlideMessage: 'Next slide',
            firstSlideMessage: 'This is the first slide',
            lastSlideMessage: 'This is the last slide',
            paginationBulletMessage: 'Go to slide {{index}}',
          },
          threshold: 5,
          longSwipesRatio: 0.05,
          longSwipesMs: 500,
        };
      }

      getThumbnailSliderOptions() {
        return {
          initialSlide: this.mainSliderSettings.initialSlide,
          loop: true,
          spaceBetween: 0,
          freeMode: true,
          slidesPerView: 'auto',
          navigation: false,
          slideToClickedSlide: true,
        };
      }

      initSliders() {
        if (this.mainSliderSettings.enableThumbnail && this.sliderThumbnailElement) {
          this.thumbnailSlider = new Swiper(this.sliderThumbnailElement, this.getThumbnailSliderOptions());

          this.mainSliderOptions.thumbs = {
            swiper: this.thumbnailSlider,
          };
        }

        this.mainSlider = new Swiper(this.sliderElement, this.mainSliderOptions);

        this.mainSlider.on('slideChange', () => {
          this.mainSlider.updateAutoHeight();

          if (this.thumbnailSlider) {
            const realIndex = this.mainSlider.realIndex;
            const targetSlide = this.thumbnailSlider.slides[realIndex];
            if (targetSlide) {
              const offset = targetSlide.offsetLeft * -1;
              this.thumbnailSlider.translateTo(offset, this.mainSliderSettings.speed || 300);
            }
          }
        });

        this.sliderElement.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', () => this.mainSlider?.updateAutoHeight(), { once: true });
          }
        });

        requestAnimationFrame(() => this.mainSlider?.updateAutoHeight());
      }

      getProductJson() {
        if (this.productJson !== undefined) return this.productJson;

        const el = document.getElementById('product-json');
        try {
          this.productJson = el ? JSON.parse(el.textContent) : null;
        } catch (e) {
          console.warn('Invalid product JSON', e);
          this.productJson = null;
        }

        return this.productJson;
      }

      getSelectedVariant() {
        const product = this.getProductJson();
        if (!product || !Array.isArray(product.variants)) return null;

        const selected = {};
        document
          .querySelectorAll('[data-product-form] .swatch input[type="radio"][data-position]:checked')
          .forEach((input) => {
            selected[`option${input.dataset.position}`] = input.value;
          });

        if (!Object.keys(selected).length) return null;

        return (
          product.variants.find((variant) =>
            Object.keys(selected).every((optionKey) => variant[optionKey] === selected[optionKey])
          ) || null
        );
      }

      bindVariantChange(imageId, variantId) {
        if (!this.mainSlider) return;

        const findSlide = (root) => {
          if (!root) return null;

          if (imageId != null && imageId !== '') {
            const byImageId = root.querySelector(
              `.swiper-slide[data-media-id="${imageId}"]:not(.swiper-slide-duplicate)`
            );
            if (byImageId) return byImageId;
          }

          if (variantId == null || variantId === '') return null;

          const variantToken = `,${variantId},`;
          return Array.from(
            root.querySelectorAll(
              '.swiper-slide[data-attached-variant-ids]:not(.swiper-slide-duplicate)'
            )
          ).find((el) => (el.dataset.attachedVariantIds || '').includes(variantToken));
        };

        const goToSlide = (swiper, slide) => {
          if (!swiper || !slide) return;

          const slides = swiper.slides || [];
          const swiperIndex = Array.prototype.indexOf.call(slides, slide);
          const speed = this.mainSliderSettings.speed || 300;

          if (swiperIndex >= 0) {
            swiper.slideTo(swiperIndex, speed);
            return;
          }

          const loopIndex = Number(slide.getAttribute('data-swiper-slide-index'));
          if (!Number.isNaN(loopIndex) && typeof swiper.slideToLoop === 'function') {
            swiper.slideToLoop(loopIndex, speed);
          }
        };

        goToSlide(this.mainSlider, findSlide(this.sliderElement));
        goToSlide(this.thumbnailSlider, findSlide(this.sliderThumbnailElement));
      }

      refreshGalleryLayout() {
        this.mainSlider?.updateAutoHeight?.();
        this.mainSlider?.update?.();
        this.thumbnailSlider?.update?.();
      }

      observeResize() {
        if (!this.mainSlider) return;

        this.onGalleryResize = () => this.refreshGalleryLayout();

        window.visualViewport?.addEventListener('resize', this.onGalleryResize);

        if (!window.ResizeObserver) return;

        this.resizeObserver = new ResizeObserver(this.onGalleryResize);
        this.resizeObserver.observe(this.sliderElement);
      }
    }
  );
}
